// src/admin/components/logs/MonthlyRevenueByUser.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    RiMoneyDollarCircleLine,
    RiUserLine,
    RiCalendarLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import {
    getMonthlyRevenueByUser,
    formatCurrency
} from '../../../models/logModel';

const MonthlyRevenueByUser = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();

    const currentDate = new Date();
    const [year, setYear] = useState(currentDate.getFullYear());
    const [month, setMonth] = useState(currentDate.getMonth() + 1);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        const fetchRevenue = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await getMonthlyRevenueByUser(year, month);
                setData(response);
            } catch (err) {
                console.error('Error fetching monthly revenue:', err);
                setError(err.message || 'Failed to load monthly revenue data');
            } finally {
                setLoading(false);
            }
        };

        fetchRevenue();
    }, [year, month]);

    const handleYearChange = (e) => {
        setYear(parseInt(e.target.value));
    };

    const handleMonthChange = (e) => {
        setMonth(parseInt(e.target.value));
    };

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const years = [];
    for (let y = currentDate.getFullYear(); y >= 2020; y--) {
        years.push(y);
    }

    const totalRevenue = data?.revenueByUser?.reduce((sum, user) => sum + parseFloat(user.totalAmount || 0), 0) || 0;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                        <RiMoneyDollarCircleLine className={`mr-3 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                        {t('monthly_revenue_by_user') || 'Monthly Revenue by User'}
                    </h1>
                    <p className={`mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                        {t('revenue_breakdown_desc') || 'View revenue breakdown by user for a specific month'}
                    </p>
                </div>
            </div>

            {/* Date Selection */}
            <div className={`p-4 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
                }`}>
                <div className="flex flex-col md:flex-row gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <RiCalendarLine className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} />
                        <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {t('select_month') || 'Select Month'}:
                        </span>
                    </div>

                    <select
                        value={month}
                        onChange={handleMonthChange}
                        className={`p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                    >
                        {months.map(m => (
                            <option key={m.value} value={m.value}>
                                {t(m.label.toLowerCase()) || m.label}
                            </option>
                        ))}
                    </select>

                    <select
                        value={year}
                        onChange={handleYearChange}
                        className={`p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            }`}
                    >
                        {years.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-red-500"></div>
                </div>
            )}

            {/* Error */}
            {error && !loading && (
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
                    } border`}>
                    {error}
                </div>
            )}

            {/* Revenue Data */}
            {!loading && !error && data && (
                <>
                    {/* Total Revenue Card */}
                    <div className={`p-6 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                    {t('total_revenue') || 'Total Revenue'}
                                </p>
                                <p className={`text-3xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {formatCurrency(totalRevenue)}
                                </p>
                                <p className={`text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                                    {t(months.find(m => m.value === month)?.label.toLowerCase())} {year}
                                </p>
                            </div>
                            <div className={`p-4 rounded-full ${isDarkMode ? 'bg-red-900/20' : 'bg-red-100'
                                }`}>
                                <RiMoneyDollarCircleLine className={`text-4xl ${isDarkMode ? 'text-red-400' : 'text-red-600'
                                    }`} />
                            </div>
                        </div>
                    </div>

                    {/* Revenue by User Table */}
                    <div className={`rounded-xl shadow overflow-hidden ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
                        }`}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className={`${isDarkMode ? 'bg-zinc-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left">{t('user') || 'User'}</th>
                                        <th scope="col" className="px-4 py-3 text-right">{t('payment_count') || 'Payment Count'}</th>
                                        <th scope="col" className="px-4 py-3 text-right">{t('total_amount') || 'Total Amount'}</th>
                                        <th scope="col" className="px-4 py-3 text-right">{t('percentage') || 'Percentage'}</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 text-gray-300' : 'divide-gray-200 text-gray-700'}`}>
                                    {data.revenueByUser && data.revenueByUser.length > 0 ? (
                                        data.revenueByUser.map((user, index) => {
                                            const percentage = totalRevenue > 0 ? ((parseFloat(user.totalAmount || 0) / totalRevenue) * 100).toFixed(1) : 0;
                                            return (
                                                <tr
                                                    key={index}
                                                    className={`${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                                                        } transition-colors duration-150`}
                                                >
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center">
                                                            <RiUserLine className="mr-2 text-gray-500" />
                                                            <span className="font-medium">{user.userName || 'Unknown'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {user.paymentCount || 0}
                                                    </td>
                                                    <td className="px-4 py-3 text-right font-medium">
                                                        {formatCurrency(user.totalAmount || 0)}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <div className={`flex-1 max-w-[100px] h-2 rounded-full ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-200'
                                                                }`}>
                                                                <div
                                                                    className={`h-2 rounded-full ${isDarkMode ? 'bg-red-500' : 'bg-red-600'
                                                                        }`}
                                                                    style={{ width: `${percentage}%` }}
                                                                ></div>
                                                            </div>
                                                            <span className="text-sm min-w-[45px]">{percentage}%</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="px-4 py-6 text-center">
                                                {t('no_revenue_data') || 'No revenue data available for this month'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MonthlyRevenueByUser;
